<template>
  <form
    id="email-communication-form"
    name="emailCommuncationForm"
    class="container mt-3"
    @submit.prevent="sendCommunication">
    <!-- Emails -->
    <div class="row">
      <div class="col">
        <label for="emails">
          Email(s):
        </label>
        <v-select
          id="emails"
          ref="emails"
          name="emails"
          placeholder="Enter one or more email addresses"
          multiple
          taggable
          no-drop
          select-on-tab
          readonly
          v-model="message.to_emails"
          @input="refocus">
        </v-select>
      </div>
    </div>
    <!-- / Emails -->

    <!-- Subject -->
    <div class="row mt-3">
      <div class="col">
        <label for="subject">Subject:</label>
        <b-form-input
          id="subject"
          ref="subject"
          name="subject"
          type="text"
          placeholder="Enter a subject"
          v-model="message.subject"
          :readonly="submitting">
        </b-form-input>
      </div>
    </div>
    <!-- / Subject -->

    <b-form-group label="Type" v-slot="Type">
      <b-form-radio
        v-model="message.type"
        :aria-describedby="'Type'"
        value="Email">
        Email
      </b-form-radio>
      <b-form-radio
        v-model="message.type"
        :aria-describedby="'Type'"
        value="Text">
        Text
      </b-form-radio>
    </b-form-group>

    <!-- Body -->
    <div class="row mt-3">
      <div class="col">
        <label for="body">Body:</label>
        <b-form-textarea
          id="body"
          ref="body"
          name="body"
          rows="5"
          v-model="message.message"
          :readonly="submitting"
          placeholder="Enter a message body"
          no-resize>
        </b-form-textarea>
      </div>
    </div>
    <!-- / Body -->

    {{ message.message.length }} / 300 characters

    <!-- Attachments -->
    <div class="row mt-3">
      <div class="col">
        <label for="attachments">
          Attached Files:
          <small class="text-danger">
            Total file size must be less than 30MB.
          </small>
        </label>

        <!-- Attachments Table -->
        <b-table
          striped
          hover
          selectable
          small
          show-empty
          select-mode="multiple"
          selected-variant=""
          sort-by="name"
          class="file-attachments"
          @row-clicked="toggleAttachment(message.attachment_uuids, $event)"
          :items="fileOptions"
          :fields="attachmentFields"
          empty-text="No attached files.">
          <template
            slot="remove"
            slot-scope="{ item }">
            <b-button
              type="button"
              size="sm"
              variant="link"
              class="py-0 px-1">
              <div v-if="isFileAttached(message.attachment_uuids, item)">
                <font-awesome-icon icon="times"></font-awesome-icon>
                Remove
              </div>
              <div v-else>
                <font-awesome-icon icon="undo"></font-awesome-icon>
                Undo
              </div>
            </b-button>
          </template>
          <template
            slot="created"
            slot-scope="{ item }">
            {{ item.created | datetime }}
          </template>
        </b-table>
        <!-- / Attachments Table -->
      </div>
    </div>
    <!-- / Attachments -->

    <!-- Attach New File -->
    <div class="row mt-3">
      <div v-if="!message.id" class="col">
        <label for="attach-new-file">
          Upload File:
          <small class="d-block d-sm-inline text-danger">
            You must click "Upload" after selecting your file in order to attach it to your message.
          </small>
        </label>
        <b-input-group>
          <b-form-file
            id="attach-new-file"
            ref="attachNewFile"
            name="attachNewFile"
            v-model="attachment"
            placeholder="Choose a file..."
            drop-placeholder="Drop file here..."
            :readonly="submitting">
          </b-form-file>
          <b-input-group-append>
            <b-button
              @click="uploadFile(message.attachment_uuids, 'organization_id', loggedInUser.actingOrg.id)"
              :disabled="!attachment || uploading"
              variant="primary">
              Upload
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </div>
    </div>
    <!-- / Attach New File -->

    <div class="corner-buttons">
      <!-- Save Event Button -->
      <b-button
        class="shadow"
        type="submit"
        variant="success"
        :disabled="isDisabled">
        <font-awesome-icon icon="paper-plane"></font-awesome-icon> Send
      </b-button>
      <!-- / Save Event Button -->
    </div>
  </form>
</template>

<script>
import _ from 'lodash';
import vSelect from 'vue-select';
import DataSvc from '../services/data';
import AppMixin from '../services/app-mixin';
import BrowserStorageMixin from '../services/browser-storage-mixin';
import DirtyForm from '../services/dirty-form';
import CommunicationMixin from '../services/communication-mixin';
import FileUploadMixin from '../services/file-upload-mixin';
import EventBus from '../services/event-bus';

export default {
  name: 'new-email-communication',
  components: {
    vSelect,
  },
  mixins: [
    AppMixin,
    BrowserStorageMixin,
    DirtyForm,
    CommunicationMixin,
    FileUploadMixin,
  ],
  computed: {
    isDisabled() {
      return this.submitting;
    },
    messageDirty() {
      const messageJSON = JSON.stringify(this.message);
      const messageCopyJSON = JSON.stringify(this.messageCopy);
      return !this.message.id && messageJSON !== messageCopyJSON;
    },
    anyDirty() {
      return this.messageDirty;
    },
  },
  methods: {
    isInvalid() {
      let invalid = false;
      if (!this.message.to_emails || this.message.to_emails.length === 0) {
        invalid = true;
        this.showWarning(
          'You must specify email addresses in order to send a message.',
          'No email addresses entered',
        );
      }
      return invalid;
    },
    sendCommunication() {
      if (this.isInvalid()) return;

      let message = _.cloneDeep(this.message);

      this.showToast('Sending message...');
      this.submitting = true;

      // if message already exists (send attempted) do a resend
      if (this.message.id) {
        message = {
          email_log_entry_id: this.message.id,
        };
      }

      message.organization_id = this.loggedInUser.actingOrg.id;

      // eslint-disable-next-line
      return DataSvc.sendCommunication(message)
        .then(() => this.delay(1000))
        .then(() => {
          this.showToast('Message sent!');
          this.messageCopy = _.cloneDeep(this.message);
          this.$router.push({
            name: 'communications',
          });
        })
        .finally(() => this.delay(1000))
        .finally(() => {
          this.submitting = false;
          this.hideToast();
        });
    },
    refocus() {
      this.$nextTick(() => {
        this.$refs.emails.$refs.search.focus();
      });
    },
  },
  created() {
    const config = this.sessionStorageGet('communication', {});

    if (config.emails) {
      this.message.to_emails = config.emails;
    } else {
      this.$router.replace({ name: 'not-found' });
    }

    // watch for org change and route to events page
    EventBus.$on('acting-org-changed', this.goToEvents);

    this.showGlobalLoader('Loading message...');
    this.initPromise.finally(this.hideGlobalLoader);
  },
  destroyed() {
    EventBus.$off('acting-org-changed', this.goToEvents);
  },
};
</script>
